import React, { useEffect, useState } from 'react';
import { BarChart3, Download, CheckCircle, Clock, XCircle } from 'lucide-react';
import { getVendorStats, getAllVendors } from '../../firebaseService';

interface VendorStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  categories: Record<string, number>;
}

const ReportsPage: React.FC = () => {
  const [stats, setStats] = useState<VendorStats | null>(null);
  const [vendorCount, setVendorCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [statsResult, vendorsResult] = await Promise.all([
          getVendorStats(),
          getAllVendors(),
        ]);
        if (statsResult.success && 'stats' in statsResult) {
          setStats(statsResult.stats);
        }
        if (vendorsResult.success && vendorsResult.vendors) {
          setVendorCount(vendorsResult.vendors.length);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getCategoryName = (category: string) =>
    category.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

  const approvalRate = stats && stats.total > 0
    ? Math.round((stats.approved / stats.total) * 100)
    : 0;

  const buildSummaryText = () => {
    if (!stats) return '';
    const lines = [
      'VENDOR MANAGEMENT REPORT',
      '========================',
      '',
      `Generated: ${new Date().toLocaleString()}`,
      '',
      'SUMMARY',
      `Total Vendors: ${stats.total}`,
      `Pending: ${stats.pending}`,
      `Approved: ${stats.approved}`,
      `Rejected: ${stats.rejected}`,
      `Approval Rate: ${approvalRate}%`,
      '',
      'CATEGORY BREAKDOWN',
      ...Object.entries(stats.categories).map(
        ([cat, count]) => `- ${getCategoryName(cat)}: ${count}`
      ),
    ];
    return lines.join('\n');
  };

  const exportSummary = () => {
    const text = buildSummaryText();
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vendor-report-${new Date().toISOString().slice(0, 10)}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sunbeth-orange-600" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-20 text-gray-500">Unable to load report data.</div>
    );
  }

  const statusBars = [
    { label: 'Pending', value: stats.pending, color: 'bg-yellow-500', icon: Clock },
    { label: 'Approved', value: stats.approved, color: 'bg-green-500', icon: CheckCircle },
    { label: 'Rejected', value: stats.rejected, color: 'bg-red-500', icon: XCircle },
  ];

  const maxStatus = Math.max(stats.pending, stats.approved, stats.rejected, 1);
  const maxCategory = Math.max(...Object.values(stats.categories), 1);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-sm text-gray-600">
            Vendor statistics and exportable summaries.
          </p>
        </div>
        <button
          type="button"
          onClick={exportSummary}
          className="px-4 py-2 bg-sunbeth-orange-600 text-white rounded-lg hover:bg-sunbeth-orange-700 flex items-center gap-2 self-start"
        >
          <Download className="h-4 w-4" />
          Export Summary
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6">
          <p className="text-sm text-gray-600">Approval Rate</p>
          <p className="text-4xl font-bold text-sunbeth-orange-600 mt-1">{approvalRate}%</p>
          <p className="text-xs text-gray-500 mt-1">
            {stats.approved} of {stats.total} vendors approved
          </p>
        </div>
        <div className="p-6 sm:border-l border-gray-200">
          <p className="text-sm text-gray-600">Total Vendors</p>
          <p className="text-4xl font-bold text-gray-900 mt-1">{vendorCount || stats.total}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-6">
          <BarChart3 className="h-5 w-5 text-sunbeth-orange-600" />
          Status Breakdown
        </h2>
        <div className="space-y-4">
          {statusBars.map((bar) => {
            const Icon = bar.icon;
            const width = `${(bar.value / maxStatus) * 100}%`;
            return (
              <div key={bar.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Icon className="h-4 w-4 text-gray-400" />
                    {bar.label}
                  </span>
                  <span className="text-sm font-bold text-gray-900">{bar.value}</span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${bar.color} rounded-full transition-all duration-500`}
                    style={{ width }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {Object.keys(stats.categories).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Category Breakdown</h2>
          <div className="space-y-3">
            {Object.entries(stats.categories)
              .sort(([, a], [, b]) => b - a)
              .map(([category, count]) => (
                <div key={category}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700">{getCategoryName(category)}</span>
                    <span className="text-sm font-bold text-gray-900">{count}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-sunbeth-orange-500 rounded-full"
                      style={{ width: `${(count / maxCategory) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Text Summary Preview</h2>
        <pre className="text-xs text-gray-600 bg-gray-50 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap font-mono">
          {buildSummaryText()}
        </pre>
      </div>
    </div>
  );
};

export default ReportsPage;
